import { compressImageIfNeeded, type ImageValidationReason, validateImageFile } from '@/app/utils/client';

/**
 * 远端 quick-case 素材落地为本地 File 的结构化结果。
 * 失败时按阶段区分，让调用方各自决定分桶（generic 失败 / imageRejected / compressFailed）。
 */
export type QuickCaseFetchResult =
  | { ok: true; file: File }
  | { ok: false; stage: 'fetch' }
  | { ok: false; stage: 'validate'; reason: ImageValidationReason }
  | { ok: false; stage: 'compress' };

export interface FetchQuickCaseFileOptions {
  /**
   * 校验通过后是否按需压缩：
   * - welcome 暂存分支只 buffer 原始 File，不压缩（false）；
   * - ready / backfill 上传分支与公共上传层口径一致，压缩后再传（true）。
   */
  compress: boolean;
}

/**
 * quick-case 三处共用的「拉取 → 包 File → 图片校验 →（按需）压缩」链路。
 *
 * - 非图片：直接放行原始 File；
 * - 图片：走 validateImageFile（同公共上传层口径），不合格回 `stage: 'validate'` + reason；
 * - compress=true 时再走 compressImageIfNeeded，失败回 `stage: 'compress'`。
 *
 * 失败的文案/分桶由调用方按 stage 处理，本函数不弹 toast、不记录列表。
 */
export async function fetchQuickCaseFile(
  url: string,
  fileName: string,
  { compress }: FetchQuickCaseFileOptions,
): Promise<QuickCaseFetchResult> {
  const resp = await fetch(url).catch(() => null);
  if (!resp?.ok) return { ok: false, stage: 'fetch' };

  const blob = await resp.blob();
  const file = new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
  if (!file.type.startsWith('image/')) return { ok: true, file };

  const validation = await validateImageFile(file);
  if (!validation.valid) return { ok: false, stage: 'validate', reason: validation.reason };
  URL.revokeObjectURL(validation.imageURL);

  if (!compress) return { ok: true, file };

  const prepared = await compressImageIfNeeded(file, {
    width: validation.imageResolution.width,
    height: validation.imageResolution.height,
  });
  if (!prepared.ok) return { ok: false, stage: 'compress' };
  return { ok: true, file: prepared.file };
}
